export type OrganizationalAliasRecord = {
  id?: number | null;
  alias: string;
  type?: string | null;
  description?: string | null;
  active?: boolean | null;
  policies?: string[] | null;
};

const normalizeWhitespace = (value: unknown) => `${value || ''}`.trim().replace(/\s+/g, ' ');

const normalizeAliasKey = (value: unknown) => normalizeWhitespace(value).toLowerCase();

export const isOrganizationalAliasName = (value: unknown) => {
  const parts = `${value || ''}`
    .split('|')
    .map((part) => normalizeWhitespace(part))
    .filter((part) => part.length > 0);
  return parts.length === 2;
};

export const buildOrganizationalAliasName = (organizationName?: string | null, roleName?: string | null) => {
  const organization = normalizeWhitespace(organizationName);
  const role = normalizeWhitespace(roleName);
  if (!organization || !role) {
    return '';
  }
  return `${organization}|${role}`;
};

export const formatOrganizationalAliasLabel = (value: unknown) => {
  const normalized = normalizeWhitespace(value);
  if (!isOrganizationalAliasName(normalized)) {
    return normalized;
  }

  const [organizationName = '', roleName = ''] = normalized.split('|').map((part) => normalizeWhitespace(part));
  return [organizationName, roleName].filter((part) => part.length > 0).join(' - ');
};

const uniqueNormalizedLabels = (values: (string | null | undefined)[]) => {
  const labels: string[] = [];
  const seen = new Set<string>();

  (values || []).forEach((value) => {
    const normalized = normalizeWhitespace(value);
    const normalizedKey = normalizeAliasKey(normalized);
    if (!normalizedKey || seen.has(normalizedKey)) {
      return;
    }
    seen.add(normalizedKey);
    labels.push(normalized);
  });

  return labels;
};

export const mergeOrganizationalAliases = (
  aliases: OrganizationalAliasRecord[],
  organizationNames: (string | null | undefined)[],
  roleNames: (string | null | undefined)[]
) => {
  const merged = new Map<string, OrganizationalAliasRecord>();

  (aliases || []).forEach((alias) => {
    const aliasName = normalizeWhitespace(alias?.alias);
    const aliasKey = normalizeAliasKey(aliasName);
    if (!aliasKey) {
      return;
    }

    merged.set(aliasKey, {
      ...alias,
      alias: aliasName,
      type: isOrganizationalAliasName(aliasName) ? 'organizational' : alias?.type
    });
  });

  const organizations = uniqueNormalizedLabels(organizationNames);
  const roles = uniqueNormalizedLabels(roleNames);

  organizations.forEach((organizationName) => {
    roles.forEach((roleName) => {
      const aliasName = buildOrganizationalAliasName(organizationName, roleName);
      const aliasKey = normalizeAliasKey(aliasName);
      if (!aliasKey) {
        return;
      }

      const existing = merged.get(aliasKey);
      if (existing) {
        merged.set(aliasKey, {
          ...existing,
          alias: existing.alias || aliasName,
          type: 'organizational'
        });
        return;
      }

      merged.set(aliasKey, {
        id: null,
        alias: aliasName,
        type: 'organizational',
        description: '',
        active: true,
        policies: []
      });
    });
  });

  return Array.from(merged.values()).sort((left, right) => left.alias.localeCompare(right.alias));
};

export const findMatchingOrganizationalAlias = (
  aliases: OrganizationalAliasRecord[],
  organizationName?: string | null,
  roleName?: string | null
) => {
  const targetKey = normalizeAliasKey(buildOrganizationalAliasName(organizationName, roleName));
  if (!targetKey) {
    return null;
  }

  const match = (aliases || []).find((alias) => {
    if (alias?.active === false) {
      return false;
    }
    if (!isOrganizationalAliasName(alias?.alias)) {
      return false;
    }
    return normalizeAliasKey(alias.alias) === targetKey;
  });

  return match || null;
};

export const expandAliasMembers = (
  aliases: OrganizationalAliasRecord[],
  aliasName?: string | null
) => {
  const targetKey = normalizeAliasKey(aliasName);
  if (!targetKey) {
    return [];
  }

  const aliasMap = new Map<string, OrganizationalAliasRecord>();
  (aliases || []).forEach((alias) => {
    const aliasKey = normalizeAliasKey(alias?.alias);
    if (!aliasKey) {
      return;
    }
    aliasMap.set(aliasKey, alias);
  });

  const root = aliasMap.get(targetKey);
  if (!root) {
    return [];
  }

  const expanded = new Set<string>();
  const visitedAliases = new Set<string>([targetKey]);

  const visitValue = (value: unknown) => {
    const normalized = normalizeWhitespace(value);
    if (!normalized) {
      return;
    }

    const aliasKey = normalizeAliasKey(normalized);
    const alias = aliasMap.get(aliasKey);
    if (alias) {
      if (visitedAliases.has(aliasKey)) {
        return;
      }
      visitedAliases.add(aliasKey);
      const members = Array.isArray(alias.policies) ? alias.policies : [];
      members.forEach((member) => visitValue(member));
      return;
    }

    expanded.add(normalized);
  };

  const rootMembers = Array.isArray(root.policies) ? root.policies : [];
  rootMembers.forEach((member) => visitValue(member));

  return Array.from(expanded);
};
